<script setup lang="ts">
import { ref, type Ref } from 'vue'
import BillingUsage from '@/components/BillingUsage.vue'
import SubscriptionInfo from '@/components/SubscriptionInfo.vue'
import TeamInfo from '@/components/TeamInfo.vue'
import TeamModal from '@/components/TeamModal.vue'
import UsageHistory from '@/components/UsageHistory.vue'
import { myRequest } from '@/utils/fetch'
import OGHeader from '@/components/OGHeader.vue'
type ISOString = string

export type UserSubscripiton = {
  current_period_start: ISOString
  current_period_end: ISOString
  billingInterval: 'monthly' | 'yearly'
  lastUsageReset: ISOString | null
  status: string
  setupPayment: boolean
  trialEnd: ISOString | null
  seats: number
  profileAddOn: number
  teamID: number
  plan: string
}

const enableTeams = false
const progress = ref(0)
const teamModal = ref(false)
const seats = ref(1)
const hasTeam = ref(false)
const teamName = ref('')
const progressLabel = ref('? / ?')
const remaining = ref(0)
const userSubscription: Ref<UserSubscripiton | null> = ref(null)
const usageHistory = ref([])

function openTeamModal() {
  teamModal.value = true
}

async function gotoBillingPortal() {
  const { url } = await myRequest('POST', '/payments/billingPortal')
  window.location.href = url
}

async function upgradeToTeam() {
  if (teamName.value === '' || seats.value < 1) {
    alert('Please fill out all fields correctly')
    return
  }
  await myRequest('POST', '/payments/upgradeToTeam', { teamName: teamName.value, seats: seats.value })
  getSubscriptionInfo()
  teamModal.value = false
}

async function getSubscriptionInfo(next?: () => void) {
  const { usage, subscription, team, outOf } = await myRequest('GET', '/payments/subscriptionInfo')
  userSubscription.value = subscription
  const totalUsage = usage.reduce((acc: number, curr: { count: number }) => acc + curr.count, 0)
  progress.value = totalUsage / outOf
  progressLabel.value = `${totalUsage} / ${outOf}`
  remaining.value = outOf - totalUsage
  hasTeam.value = !!team
  usageHistory.value = usage
  if (next) next()
}
getSubscriptionInfo()
</script>

<template>
  <div>
    <OGHeader title="Billing" subtitle="Manage your billing and subscription" />
    <div class="row q-col-gutter-md full-width">
      <BillingUsage
        :userSubscription="userSubscription"
        :progress="progress"
        :progressLabel="progressLabel"
        :remaining="remaining"
        @getSubscriptionInfo="getSubscriptionInfo"
      />
      <SubscriptionInfo :userSubscription="userSubscription" />
    </div>
    <div class="row q-pt-md full-width justify-center">
      <UsageHistory class="col-12" :usageData="usageHistory" />
      <div class="q-pt-md">
        <q-btn @click="gotoBillingPortal" color="secondary" label="Access Billing Portal" />
      </div>
    </div>

    <div v-if="enableTeams">
      <div class="row col-12" v-if="!hasTeam && enableTeams">
        <q-card class="myCard" bordered>
          <q-card-section>
            <h6>Team</h6>
            <p>
              You have a team you want to manage billing for? Upgrade to a team account. Team account includes team
              insights and billing for an extra $1 per month or $10 per year. Upgrading to a team account is
              irreversible.
            </p>
            <q-btn @click="openTeamModal" class="full-width" color="primary" label="Upgrade to team account" />
          </q-card-section>
        </q-card>
      </div>
      <TeamInfo v-if="hasTeam" :userSubscription="userSubscription" />

      <TeamModal v-model:teamModal="teamModal" :teamName="teamName" :seats="seats" @upgradeToTeam="upgradeToTeam" />
    </div>
  </div>
</template>
